<template>
  <lf-modal :close>
    <loader :is-loading="documentsState.gettingUploadedDocs" />
    <template #header>
      <div class="w-full flex items-center justify-between mb-6">
        <lf-h1>{{ $t("DEALS.PLACEMENT.SEND_ADDITIONAL_DOCUMENTS") }}</lf-h1>
        <div class="flex space-x-2">
          <lf-button
            data-cy="select-all"
            class="text-label border"
            @click="selectAll"
          >
            {{ $t("COMMON.SELECT_ALL") }}
          </lf-button>
          <label
            data-cy="upload-file"
            for="upload-additional-file"
            class="text-primary bg-primary/10 flex cursor-pointer rounded-sm whitespace-nowrap justify-center justify-items-center items-center text-center px-6 py-2-5 font-medium"
          >
            {{ $t("COMMON.NEW") }} {{ $t("COMMON.DOCUMENT") }}
          </label>
          <input
            id="upload-additional-file"
            data-cy="upload-file"
            :accept="ACCEPT"
            class="hidden"
            multiple
            type="file"
            @change="handleFileChange"
          />
        </div>
      </div>
    </template>
    <template #content>
      <div class="p-6 flex flex-col space-y-6">
        <search-input
          v-model="searchTerm"
          class="w-full"
          no-margin
          no-padding
        />
        <div v-if="filesQueue?.length">
          <div class="grid grid-cols-3 items-stretch gap-3 flex-wrap">
            <funder-upload-item
              v-for="(file, index) in filesQueue"
              :key="file.id"
              :file="file"
              :upload-triggered="uploadLoading"
              :show-loading-spinner="uploadLoading"
              :style="`z-index: ${
                filesQueue?.length ? filesQueue.length - index : 1
              }`"
              @file:cancel="removeFileFromQueue"
            />
          </div>
        </div>
        <div
          v-for="documentGroup in documentsByType"
          :key="documentGroup.dir"
          class="space-y-3"
        >
          <lf-h3 v-if="documentGroup.name">{{ documentGroup.name }}</lf-h3>
          <div class="grid grid-cols-3 items-stretch gap-3 flex-wrap">
            <div
              v-for="file in documentGroup.documents"
              :key="file.id"
              data-cy="deal-document"
              class="p-3 border rounded flex items-center gap-3"
            >
              <lf-checkbox
                :value="file.id"
                :name="`file-${file.id}`"
                :model-value="filesChecked[file.id]"
                @change="rowChanged(file.id)"
              />
              <documents-table-name
                :model="file"
                :enable-preview="false"
                use-as-description="file-type"
              />
              <lf-tag v-if="fileIsSent(file.id)">
                {{ $t("COMMON.SENT") }}
              </lf-tag>
            </div>
          </div>
        </div>
        <div class="flex justify-end items-center space-x-3">
          <lf-button class="text-label border" @click="close">
            {{ $t("COMMON.CANCEL") }}
          </lf-button>
          <primary-button :disabled="cantSend" @click="sendAndUploadDocuments">
            {{ $t("COMMON.SEND") }}
          </primary-button>
        </div>
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  FILE_TYPE_BPR_REPORT,
  FILE_TYPE_OTHER,
} from "@/helpers/constants/deals";
import { getExtensionFromName } from "@/helpers/files";
import { makeRandomString } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import { useAnalyticsStore } from "@/stores/analytics";
import { useDocumentsState } from "@/stores/documents";

import DocumentsTableName from "@/components/deals/documents/DocumentsTableName.vue";
import FunderUploadItem from "@/components/deals/placement/FunderUploadItem.vue";
import LfModal from "@/components/ui/Modal.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import LfTag from "@/components/ui/text/LfTag.vue";

import type { FileSent, FileWBlob } from "@/models/common";
import type { DocumentTableItem, StipsCollection } from "@/models/common";

interface DocumentsByStip extends StipsCollection {
  dir: string;
  documents: DocumentTableItem[];
}

const IMAGE_TYPES = ["gif", "jpeg", "jpg", "png", "heic"];
const ACCEPT = "image/*, .doc, .pdf, .xls, .xlsx, .jpg, .docx, .heic";
const MAX_FILE_SIZE = 100_000;
const MB = 1024;

const { close, filesSent, placementId } = defineProps<{
  close: () => void;
  placementId: string;
  filesSent?: FileSent[];
}>();

const { t } = useI18n();
const documentsState = useDocumentsState();
const { dispatch, getters } = useStore();
const { showMessage } = useNotification();
const { trackEvent } = useAnalyticsStore();

const searchTerm = ref("");
const filesChecked = ref<Record<string, boolean>>({});
const filesQueue = ref<FileWBlob[]>([]);

const stipCollection = computed<StipsCollection[]>(
  () => getters["options/fileTypes"] ?? []
);

const filteredDocuments = computed(() =>
  searchTerm.value
    ? documentsState.uploadedDocuments.filter((file) =>
        file.original_name
          ?.toLowerCase()
          .includes(searchTerm.value.trim().toLowerCase())
      )
    : documentsState.uploadedDocuments
);

const documentsByType = computed(() => {
  if (!filteredDocuments.value.length) {
    return [];
  }

  const documents = stipCollection.value.reduce<DocumentsByStip[]>(
    (acum, stip) => {
      const docs = filteredDocuments.value.filter(
        (doc) => doc.file_type && stip.types.includes(doc.file_type)
      );
      if (docs.length) {
        acum.push({
          ...stip,
          documents: docs,
        } as DocumentsByStip);
      }
      return acum;
    },
    []
  );

  const docsWithoutType = filteredDocuments.value.filter(
    (doc) => !doc.file_type || doc.file_type === FILE_TYPE_BPR_REPORT
  );

  if (docsWithoutType.length) {
    documents.unshift({
      dir: FILE_TYPE_OTHER,
      documents: docsWithoutType,
    } as DocumentsByStip);
  }

  return documents;
});

const filesSentIds = computed(() => {
  return (filesSent || []).flatMap(({ file_id }) => file_id);
});

const cantSend = computed(() => {
  return (
    Object.values(filesChecked.value)?.every((file) => file === false) &&
    !filesQueue.value.length
  );
});

const fileIsSent = (id: string | number) => filesSentIds.value.indexOf(id) > -1;

const rowChanged = (id: string | number) => {
  filesChecked.value[id] = !filesChecked.value[id];
};

const handleFileChange = (e: Event) => {
  enqueueFiles((e?.target as HTMLInputElement)?.files);
  // clear the input value because otherwise
  // onChange won't work next time you select the same file
  (e.target as HTMLInputElement).value = "";
};

const getFilesWithValidSize = (files: FileList | File[]) => {
  const filesArray = Object.values(files);
  const filesWithValidSize = filesArray.filter(
    (file) => Math.round(file.size / MB) < MAX_FILE_SIZE
  );
  if (filesArray?.length !== filesWithValidSize?.length) {
    showMessage(
      t("DEALS.DOCUMENTS.FILE_BIG"),
      "error",
      t("DEALS.DOCUMENTS.FILE_BIG_SUBTITLE")
    );
  }
  return filesWithValidSize;
};

const enqueueFiles = (files: FileList | File[] | null | undefined) => {
  if (!files) {
    return;
  }
  const toUpload = getFilesWithValidSize(files);
  filesQueue.value = [
    ...filesQueue.value,
    ...toUpload.map((file) => {
      const type = getExtensionFromName(file.name);
      return {
        id: makeRandomString(),
        name: file.name,
        progress: 0,
        failed: false,
        uploaded: false,
        size: Math.round(file.size / 1024), // in kbytes
        type,
        documentType: null,
        imagePreviewUrl: IMAGE_TYPES.includes(type)
          ? URL.createObjectURL(file)
          : null,
        blob: file,
      };
    }),
  ];
};

const { loading: uploadLoading, fetchWrapper: uploadQueuedDocuments } =
  usePromiseWrapper(
    async () => {
      for (const file of filesQueue.value) {
        file.failed = false;
      }

      const filesPayload: File[] = filesQueue.value.flatMap((file) => {
        return file.blob;
      });

      await dispatch("applications/uploadPlacementFiles", {
        placementId,
        data: filesPayload,
      });

      for (const file of filesQueue.value) {
        file.uploaded = true;
      }
    },
    {
      onError: () => {
        for (const file of filesQueue.value) {
          file.failed = true;
        }
      },
    }
  );

const getIndexOfFileInQueue = (id: string | undefined) => {
  if (!id) {
    return -1;
  }

  return filesQueue.value.findIndex((file) => file.id === id);
};

const removeFileFromQueue = (id: string) => {
  const fileIndex = getIndexOfFileInQueue(id);

  if (fileIndex === -1) {
    return;
  }
  return filesQueue.value.splice(fileIndex, 1);
};

const selectAll = () => {
  filteredDocuments.value.forEach((file) => {
    filesChecked.value[file.id] = true;
  });
};

const sendAndUploadDocuments = async () => {
  // check if any files are checked, send those
  const actions = [];
  const checkedFileIds = Object.entries(filesChecked.value)
    .filter(([, isChecked]) => isChecked)
    .map(([fileId]) => Number(fileId));

  if (checkedFileIds.length) {
    actions.push(
      dispatch("applications/sendPlacementFiles", {
        placementId,
        fileIds: checkedFileIds,
      })
    );
  }

  // then upload any queued documents if needed.
  if (filesQueue.value.length) {
    actions.push(uploadQueuedDocuments());
  }

  if (actions.length) {
    try {
      await Promise.all(actions);

      if (checkedFileIds.length) {
        trackEvent(AnalyticsEvent.PlacementAdditionalDocsSent, {
          placement_id: placementId,
          files_count: checkedFileIds.length,
        });
      }

      if (filesQueue.value.length) {
        trackEvent(AnalyticsEvent.PlacementAdditionalDocsUploaded, {
          placement_id: placementId,
          files_count: filesQueue.value.length,
        });
      }

      dispatch("applications/getPlacements");
      documentsState.getUploadedDocuments();
      close();
    } catch (e) {
      showMessage(t("DEALS.PLACEMENT.DOCUMENTS_SEND_FAILED"), "error");
    }
  }
};
</script>
